import { Box, Stack, Typography } from '@mui/material';

import { getEnvironmentVariable } from '../../Utils/environment';
import InfoscreenActivityChart from '../Charts/ApexCharts/InfoscreenActivityChart';
import InfoscreenRoomCalendarView from './InfoscreenRoomCalendarView';
import InfoscreenSpectrumMapView, { type SpectrumMapTarget } from './InfoscreenSpectrumMapView';
import InfoscreenSpectrumView from './InfoscreenSpectrumView';
import WidgetRenderer from './Widget/WidgetRenderer';
import YouTubePlayer from './YouTubePlayer';

import type { Dashboard, infoscreenLayoutView, SiteSelect } from '../../Types/AuthServer';

interface InfoscreenFullscreenViewProps {
    enableLive?: boolean;
    infoscreenLayout: Dashboard['socials'];
    isActiveView?: boolean;
    onSpectrumMapNavigate?: (viewIndex: number) => void;
    showBackgroundForImages?: boolean;
    sites: SiteSelect[];
    spectrumMapTargets?: SpectrumMapTarget[];
    view: infoscreenLayoutView;
}

export default function InfoscreenFullscreenView({
    enableLive = true,
    infoscreenLayout,
    isActiveView = true,
    onSpectrumMapNavigate,
    showBackgroundForImages = false,
    sites,
    spectrumMapTargets = [],
    view,
}: Readonly<InfoscreenFullscreenViewProps>) {
    const basePath = getEnvironmentVariable('EXPO_PUBLIC_IMAGE_SERVE_HOST') ?? '';

    if (view.type === 'image') {
        const imageUrl = view.imageConfig?.url ? `${basePath}/${view.imageConfig.url}.webp` : '';
        return (
            <Box
                sx={{
                    alignItems: 'center',
                    backgroundColor: showBackgroundForImages ? 'transparent' : '#000000',
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    position: 'relative',
                    width: '100%',
                }}
            >
                {imageUrl ? (
                    <Box
                        alt="Infoscreen content"
                        component="img"
                        src={imageUrl}
                        sx={{
                            height: 'auto',
                            maxHeight: '100%',
                            maxWidth: '100%',
                            objectFit: 'contain',
                            width: 'auto',
                        }}
                    />
                ) : (
                    <Typography
                        sx={{
                            color: '#ffffff',
                            textAlign: 'center',
                        }}
                        variant="h4"
                    >
                        No image selected
                    </Typography>
                )}
            </Box>
        );
    }

    if (view.type === 'graph') {
        const graphConfig = view.graphConfig;

        // If no chart configuration is available or siteId is missing, fall back to widget renderer
        if (!graphConfig?.siteId || graphConfig.siteId === '') {
            return (
                <Box
                    sx={{
                        alignItems: 'center',
                        backgroundColor: showBackgroundForImages ? 'transparent' : '#000000',
                        display: 'flex',
                        height: '100%',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <WidgetRenderer selectedWidget={view.graphConfig?.url || null} sites={sites} />
                </Box>
            );
        }

        // Use the InfoscreenActivityChart for configured charts
        return (
            <Box
                sx={{
                    backgroundColor:
                        graphConfig.backgroundColor || (showBackgroundForImages ? 'transparent' : '#000000'),
                    height: '100%',
                    width: '100%',
                }}
            >
                <InfoscreenActivityChart
                    config={{
                        chartType: graphConfig.chartType || 'line',
                        dataSource: graphConfig.dataSource || 'simple',
                        interval: graphConfig.interval || 'day',
                        siteId: graphConfig.siteId,
                        timeFilter: graphConfig.timeFilter || '24h',
                        title: graphConfig.title || '',
                    }}
                    infoscreenLayout={{
                        backgroundColor: graphConfig.backgroundColor || infoscreenLayout?.backgroundColor,
                        textColor: infoscreenLayout?.textColor,
                        titleColor: infoscreenLayout?.titleColor,
                    }}
                />
            </Box>
        );
    }

    if (view.type === 'video') {
        return (
            <YouTubePlayer
                showBackgroundForImages={showBackgroundForImages}
                url={view.videoConfig?.url || ''}
                videoConfig={view.videoConfig}
            />
        );
    }

    if (view.type === 'roomCalendar') {
        return (
            <Box
                sx={{ backgroundColor: infoscreenLayout?.backgroundColor || '#000000', height: '100%', width: '100%' }}
            >
                <InfoscreenRoomCalendarView
                    infoscreenLayout={infoscreenLayout}
                    roomCalendarConfig={view.roomCalendarConfig}
                />
            </Box>
        );
    }

    if (view.type === 'spectrum') {
        return (
            <Box sx={{ backgroundColor: '#000000', height: '100%', width: '100%' }}>
                <InfoscreenSpectrumView
                    enableLive={enableLive}
                    infoscreenLayout={infoscreenLayout}
                    isActive={isActiveView}
                    sensorSerial={view.spectrumConfig?.sensorSerial || null}
                />
            </Box>
        );
    }

    if (view.type === 'spectrumMap') {
        return (
            <InfoscreenSpectrumMapView
                infoscreenLayout={infoscreenLayout}
                mapTargets={spectrumMapTargets}
                onNavigateToView={(viewIndex) => {
                    if (onSpectrumMapNavigate) {
                        onSpectrumMapNavigate(viewIndex);
                    }
                }}
            />
        );
    }

    // Fallback for unsupported view types
    return (
        <Stack
            sx={{
                alignItems: 'center',
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
            }}
        >
            <Typography color={infoscreenLayout?.textColor || '#ffffff'} variant="h4">
                Unsupported fullscreen view type: {view.type}
            </Typography>
        </Stack>
    );
}
